import { useCallback, useEffect, useRef } from 'react';

import { SaveRefused } from 'hooks/useAutoSave';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { STORE_SCREEN } from 'utils/storeScreen';

/**
 * D10, scene 14: a store from a screen that is out of date is refused.
 *
 * The server counts the stores of each thing a screen edits. The screen
 * asks for the count as it opens, sends it with every store ("built on"),
 * and takes the new count from each of its own stores. When someone else
 * stored meanwhile the counts differ, the store is refused, and the person
 * chooses "Load theirs" or "Keep mine" (which sends the store marked as
 * overwriting).
 */
export type KeptThing =
  | 'ads'
  | 'interview'
  | 'preferences'
  | 'brand'
  | 'note'
  | 'template'
  | 'account'
  | 'customer';

export interface BasedOn {
  version: number | null;
  overwrite?: boolean;
}

/** What a store function needs for D10: what it is built on, and where the new count goes. */
export interface OutOfDateGuard {
  basedOn: BasedOn;
  stored: (version: number | null) => void;
}

/** The count the server holds now; null when it could not be asked. */
export const getKeptVersion = async (
  thing: KeptThing,
  id: number | string
): Promise<number | null> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  try {
    const response = await fetch(`${base}/v4/kept-versions/${thing}/${id}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
        'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
      },
    });
    if (!response.ok) return null;
    const answer = (await response.json()) as {
      data?: { kept_version?: number };
    };
    return typeof answer.data?.kept_version === 'number'
      ? answer.data.kept_version
      : null;
  } catch (_e) {
    return null;
  }
};

/** The store's body, saying what it is built on. Without a count: as before. */
export const withBasedOn = (
  body: Record<string, unknown>,
  basedOn?: BasedOn
): Record<string, unknown> => ({
  ...body,
  // Always: which screen this store comes from.
  store_screen: STORE_SCREEN,
  ...(basedOn && basedOn.version !== null ? { based_on: basedOn.version } : {}),
  ...(basedOn?.overwrite ? { overwrite: true } : {}),
});

/**
 * Reads a store's answer for D10. @throws SaveRefused when the server
 * refused it as out of date. Otherwise answers the new count, when given.
 */
export const refusedOrVersion = async (
  response: Response
): Promise<number | null> => {
  if (response.status === 409) {
    const refused = (await response
      .clone()
      .json()
      .catch(() => ({}))) as {
      out_of_date?: boolean;
      data?: {
        by?: string | null;
        own?: boolean;
        at?: string | null;
        kept_version?: number;
      };
    };
    if (refused.out_of_date) {
      throw new SaveRefused({
        by: refused.data?.by ?? null,
        own: refused.data?.own === true,
        at: refused.data?.at ?? null,
        keptVersion: Number(refused.data?.kept_version ?? 0),
      });
    }
  }
  const version = Number(response.headers.get('X-Kept-Version'));
  return response.ok && Number.isFinite(version) && version > 0
    ? version
    : null;
};

/**
 * What a screen needs for D10: the count it builds on (asked for when the
 * thing is opened, then taken from its own stores) and the "Keep mine" mark
 * for its next store. `id` is null while nothing is open.
 */
export const useKeptVersion = (
  thing: KeptThing,
  id: number | string | null | undefined
) => {
  const builtOn = useRef(new Map<string, number>());
  const overwriteOnce = useRef(false);
  const key = id === null || id === undefined ? null : `${thing}:${id}`;

  useEffect(() => {
    // Another thing: a "Keep mine" that never went through does not travel.
    overwriteOnce.current = false;
    if (key === null || id === null || id === undefined) return undefined;
    let current = true;
    // Opened (again): the count belongs to the data that is loaded now, not
    // to an earlier visit in this session.
    builtOn.current.delete(key);
    getKeptVersion(thing, id).then((version) => {
      // Never over a count this screen got from a store of its own meanwhile.
      if (current && version !== null && !builtOn.current.has(key)) {
        builtOn.current.set(key, version);
      }
    });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /** What the next store of this thing is built on. */
  const basedOn = useCallback(
    (forId: number | string): BasedOn => ({
      version: builtOn.current.get(`${thing}:${forId}`) ?? null,
      overwrite: overwriteOnce.current,
    }),
    [thing]
  );
  /** A store of this screen landed, with the count it now has. */
  const stored = useCallback(
    (forId: number | string, version: number | null) => {
      overwriteOnce.current = false;
      if (version !== null) builtOn.current.set(`${thing}:${forId}`, version);
    },
    [thing]
  );
  /** "Keep mine": the next store goes over theirs, knowingly. */
  const keepMine = useCallback(() => {
    overwriteOnce.current = true;
  }, []);

  return { basedOn, stored, keepMine };
};
